"use client"

import type { CSSProperties } from "react"

export interface SliderProps {
  value: number
  onChange: (next: number) => void
  /** Se llama al SOLTAR (pointerup/keyup), para persistir una sola vez y no
   *  disparar un guardado por cada píxel que se arrastra. */
  onCommit?: (next: number) => void
  min?: number
  max?: number
  step?: number
  /** Rótulo del extremo izquierdo (menos) y derecho (más). */
  minLabel?: string
  maxLabel?: string
  disabled?: boolean
  "aria-label"?: string
}

/**
 * Control de rango del DS. Nativo `input[type=range]` a propósito: trae gratis
 * el arrastre táctil, las flechas del teclado y el rol ARIA correcto — un
 * reimplementado con divs pierde las tres cosas.
 *
 * `onChange` va en cada movimiento (para la vista previa en vivo) y `onCommit`
 * solo al soltar (para guardar).
 */
export function Slider({
  value,
  onChange,
  onCommit,
  min = 0,
  max = 100,
  step = 1,
  minLabel,
  maxLabel,
  disabled = false,
  ...rest
}: SliderProps) {
  const pct = max === min ? 0 : ((value - min) / (max - min)) * 100
  return (
    <div className="w-full">
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        onPointerUp={(e) => onCommit?.(Number((e.target as HTMLInputElement).value))}
        onKeyUp={(e) => onCommit?.(Number((e.target as HTMLInputElement).value))}
        className="redy-slider w-full cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
        style={{ "--redy-slider-pct": `${pct}%` } as CSSProperties}
        {...rest}
      />
      {(minLabel || maxLabel) && (
        <div className="mt-1.5 flex justify-between text-[10.5px] text-[var(--redy-muted)]">
          <span>{minLabel}</span>
          <span>{maxLabel}</span>
        </div>
      )}
      {/* La pista y el pulgar de un range no se pueden pintar con clases: hay
          que llegar a los pseudo-elementos de cada motor. */}
      <style jsx>{`
        .redy-slider {
          -webkit-appearance: none;
          appearance: none;
          height: 22px;
          background: transparent;
        }
        .redy-slider::-webkit-slider-runnable-track {
          height: 4px;
          border-radius: 999px;
          background: linear-gradient(
            to right,
            var(--redy-red) var(--redy-slider-pct),
            var(--redy-border) var(--redy-slider-pct)
          );
        }
        .redy-slider::-moz-range-track {
          height: 4px;
          border-radius: 999px;
          background: var(--redy-border);
        }
        .redy-slider::-moz-range-progress {
          height: 4px;
          border-radius: 999px;
          background: var(--redy-red);
        }
        .redy-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          margin-top: -7px;
          border-radius: 999px;
          background: #fff;
          border: 2px solid var(--redy-red);
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
        }
        .redy-slider::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 999px;
          background: #fff;
          border: 2px solid var(--redy-red);
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
        }
        .redy-slider:focus-visible::-webkit-slider-thumb {
          outline: 2px solid var(--redy-red);
          outline-offset: 2px;
        }
        .redy-slider:focus-visible::-moz-range-thumb {
          outline: 2px solid var(--redy-red);
          outline-offset: 2px;
        }
      `}</style>
    </div>
  )
}
